import { ActionContext, createStore } from "vuex";
import {
  Sale,
  InitialResponse,
  Settings,
  BidderCount,
  Bidder,
  ClerkingMessage, SystemNotification
} from "@/types/types";
import { DARStore } from "@/types/store";
import createWebSocketPlugin from "@/store/plugins/websocket";
import { moduleLots } from "@/store/modules/lots";

const state: DARStore = {
  lots: [],
  bidders: [],
  bidderCount: {
    online: 0,
    phone: 0,
    present: 0,
    absentee: 0,
    countries: []
  },
  bidIncrements: [],
  bidIncrementsSteps: [],
  broadcastMessages: [],
  broadcastMessage: [],
  settings: {
    showBiddersNamesAuctioneer: false,
    showBiddersNamesClerk: true,
    showCountryCode: true,
    showCountryFlag: true,
    showRoomScreenFooter: false,
    showRoomScreenHeader: false
  },
  isConnected: true,
  isSaleProvisional: false,
  isMuted: true,
  onlineBidNotificationCounter: 0,
  isStreaming: false,
  isInitialLoad: true,
  lastPingTime: 0,
  lastPing: 0,
  isStreamServiceReady: false,
  partnerTicker: "",
  systemNotifications: []
};

export default createStore({
  state,
  mutations: {
    setupSale(state, sale: Sale) {
      state.sale = sale;
    },
    setupSettings(state, settings: Settings) {
      state.settings = settings;
    },
    setToken(state, token: string) {
      state.clientToken = token;
    },
    setInitialLoad(state, load: boolean) {
      state.isInitialLoad = load;
    },
    setBidderCount(state, bidderCount: BidderCount) {
      state.bidderCount = bidderCount;
    },
    setBidIncrements(state, bidIncrements: Array<Array<number>>) {
      state.bidIncrements = bidIncrements[0];
      state.bidIncrementsSteps = bidIncrements[1];
    },
    setBidders(state, bidders: Array<Bidder>) {
      state.bidders = bidders;
    },
    setBidder(state, bidder: Bidder) {
      state.bidder = bidder;
    },
    setBroadcastMessage(state, broadcastMessage: ClerkingMessage) {
      state.broadcastMessage.unshift(broadcastMessage);
      state.broadcastMessage = state.broadcastMessage.slice(0, 5);
    },
    hideBroadcastMessage(state, broadcastMessage?: string) {
      state.broadcastMessage.splice(
        state.broadcastMessage.findIndex(msg => msg.id === broadcastMessage),
        1
      );
    },
    clearBroadcastMessage(state) {
      state.broadcastMessage = [];
    },
    setBroadcastMessages(state, broadcastMessages: Array<string>) {
      state.broadcastMessages = broadcastMessages;
    },
    setConnected(state, isConnected: boolean) {
      state.isConnected = isConnected;
    },
    setIsSaleProvisional(state, isSaleProvisional: boolean) {
      state.isSaleProvisional = isSaleProvisional;
    },
    setIsMuted(state, isMuted: boolean) {
      state.isMuted = isMuted;
    },
    setOnlineBidNotificationCounter(state, count: number) {
      state.onlineBidNotificationCounter = count;
    },
    setStreamStatus(state, isStreaming: boolean) {
      state.isStreaming = isStreaming;
    },
    setLastMessageTime(state, lastMessageTime: number) {
      state.lastMessageTime = lastMessageTime;
    },
    setPingTime(state, lastPingTime: number) {
      state.lastPingTime = lastPingTime;
    },
    setPing(state, lastPing: number) {
      state.lastPing = lastPing;
    },
    setStreamServiceReady(state, isReady: boolean) {
      state.isStreamServiceReady = isReady;
    },
    setPartnerTicker(state, partnerTicker: string) {
      state.partnerTicker = partnerTicker;
    },
    addSystemNotification(state, systemNotification: SystemNotification) {
      state.systemNotifications.unshift(systemNotification);
    }
  },
  actions: {
    initialSetup({ commit, getters }, newState: InitialResponse) {
      commit("setPartnerTicker", newState.partnerTicker);
      commit("setupSale", newState.sale);
      commit("setupSettings", newState.settings);
      // commit("setToken", newState.websocketAuthToken);
      commit("setBidderCount", newState.bidderCount);
      commit("setBidIncrements", [
        newState.bidIncrements,
        newState.bidIncrementsSteps
      ]);
      commit("setBidders", newState.bidders);
      commit("setBidder", newState.bidder);
      commit("setBroadcastMessages", newState.broadcastMessages);
      commit("setStreamStatus", newState.sale?.isStreaming || false);

      // This is to ensure consistency. We always keep next and previous lot
      // according to the data we get from server, it should not be possible
      // to differ from the next and previous lot that are on current lot.
      // We only allow to set Previous and Next here, if client joins before
      // or after the sale has ended.

      if (newState.lots) {
        newState.lots.forEach(lot => commit("addLot", lot));
      }

      if (newState.previousLot) {
        commit("addLot", newState.previousLot);
        commit("setPreviousLot", getters.lot(newState.previousLot.number));
      }

      if (newState.nextLot) {
        commit("addLot", newState.nextLot);
        commit("setNextLot", getters.lot(newState.nextLot.number));
      }

      if (newState.currentLot) {
        commit("addLot", newState.currentLot);
        commit("setCurrentLot", getters.lot(newState.currentLot.number));
      }
      commit("setInitialLoad", false);
    },
    setBidderCount({ commit }, { bidderCount }) {
      commit("setBidderCount", bidderCount);
    },
    setIsSaleProvisional({ commit }, { isSaleProvisional }) {
      commit("setIsSaleProvisional", isSaleProvisional);
    },
    async hideBroadcastMessage({ commit }, { message }) {
      commit("hideBroadcastMessage", message);
    },
    async clearBroadcastMessage({ commit }) {
      commit("clearBroadcastMessage");
    },
    async clerkBroadcastMessage(
      { commit, getters, dispatch }: ActionContext<DARStore, DARStore>,
      { message, isAlert }: { message: string; isAlert: boolean }
    ) {
      console.log("clerk broadcast message", isAlert, message);
    },
    async clerkSaveBroadcastMessage(
      { commit, getters, dispatch }: ActionContext<DARStore, DARStore>,
      { message }: { message: string }
    ) {
      console.log("clerk add broadcast message", message);
    },
    async clerkRemoveBroadcastMessage(
      { commit, getters, dispatch }: ActionContext<DARStore, DARStore>,
      { message }: { message: string }
    ) {
      console.log("clerk Remove broadcast message", message);
    },
    async clerkFairWarn(
      { commit, getters, dispatch }: ActionContext<DARStore, DARStore>,
      { lotNumber, level }: { lotNumber: string; level: number }
    ) {
      console.log("clerk fair warning", lotNumber, level);
    },
    async clerkSetPause(
      { commit, getters, dispatch }: ActionContext<DARStore, DARStore>,
      { isPause }: { isPause: boolean }
    ) {
      console.log("clerk set pause", isPause);
    },
    async resetLot(
      { commit, getters, dispatch }: ActionContext<DARStore, DARStore>,
      { lotNumber }: { lotNumber: string }
    ) {
      console.log("clerk reset lot", lotNumber);
    },
    async finishAuction(
      { commit, getters, dispatch }: ActionContext<DARStore, DARStore>,
      { isFinished }: { isFinished: boolean }
    ) {
      console.log("clerk finish Auction", isFinished);
    },
    async setSaleIsManualIncrement(
      { commit, getters, dispatch }: ActionContext<DARStore, DARStore>,
      {
        isManualIncrement,
        value
      }: { isManualIncrement: boolean; value: number }
    ) {
      const sale = getters.sale;
      sale.isManualIncrement = isManualIncrement;
      commit("setupSale", sale);
    },
    async addManualIncrement(
      { commit, getters, dispatch }: ActionContext<DARStore, DARStore>,
      { value }: { value: number }
    ) {
      console.log("add manual increment", value);
    },
    async removeManualIncrement(
      { commit, getters, dispatch }: ActionContext<DARStore, DARStore>,
      { value }: { value: number }
    ) {
      console.log("remove manual increment", value);
    },
    async setMuted(
      { commit, getters, dispatch }: ActionContext<DARStore, DARStore>,
      value
    ) {
      commit("setIsMuted", value);
    },
    async setStreaming(
      { commit, getters, dispatch }: ActionContext<DARStore, DARStore>,
      value: boolean
    ) {
      commit("setStreamStatus", value);
    },
    async logStreamStatistics(
      { commit, getters, dispatch }: ActionContext<DARStore, DARStore>,
      value: object
    ) {
      return;
    }
  },
  modules: { lots: moduleLots },
  getters: {
    sale: state => state.sale,
    bidderCount: state => state.bidderCount,
    bidIncrements: state => state.bidIncrements,
    bidIncrementsSteps: state => state.bidIncrementsSteps,
    bidders: state => state.bidders,
    bidderCountAll: state =>
      state.bidderCount.online +
      state.bidderCount.phone +
      state.bidderCount.present +
      state.bidderCount.absentee,
    clientToken: state => state.clientToken,
    broadcastMessage: state => state.broadcastMessage,
    broadcastMessages: state => state.broadcastMessages,
    settings: state => state.settings,
    isConnected: state => state.isConnected,
    bidder: state => state.bidder,
    isSaleProvisional: state => state.isSaleProvisional,
    isMuted: state => state.isMuted,
    isStreaming: state => state.isStreaming,
    ping: state => state.lastPing,
    isInitialLoad: state => state.isInitialLoad,
    onlineBidNotificationCounter: state => state.onlineBidNotificationCounter,
    isStreamServiceReady: state => state.isStreamServiceReady,
    partnerTicker: state => state.partnerTicker,
    systemNotifications: state => state.systemNotifications
  },
  plugins: [createWebSocketPlugin()]
});
